import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import AuthDialog from "@/components/AuthDialog";
import PaymentDialog from "@/components/PaymentDialog";
import CandlestickChart from "@/components/CandlestickChart";
import AssetPriceCard from "@/components/asset/AssetPriceCard";
import AssetActiveAlerts from "@/components/asset/AssetActiveAlerts";
import AssetPredictionCard from "@/components/asset/AssetPredictionCard";
import { AssetAlertDialog, AssetPortfolioDialog } from "@/components/asset/AssetActionDialogs";
import { useAssetDetail } from "@/hooks/useAssetDetail";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-effect p-3 rounded-lg border border-white/10">
        <p className="text-sm text-gray-400">{payload[0].payload.date}</p>
        <p className="text-lg font-bold text-[#00E0FF] mono">
          ${payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

export default function AssetDetail() {
  const { symbol } = useParams();
  const navigate = useNavigate();
  useAuth(); // Subscribe so token changes trigger re-renders

  const {
    assetData,
    loading,
    prediction,
    predictionLoading,
    alerts,
    chartDays,
    setChartDays,
    ohlcData,
    indicatorData,
    fetchIndicators,
    generatePrediction,
    addToPortfolio,
    createAlert,
    deleteAlert,
  } = useAssetDetail(symbol);

  const [authDialog, setAuthDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [alertDialog, setAlertDialog] = useState(false);
  const [portfolioDialog, setPortfolioDialog] = useState(false);

  const handleIndicatorParamsChange = useCallback((newParams) => {
    fetchIndicators(chartDays, newParams);
  }, [fetchIndicators, chartDays]);

  const handleGenerate = useCallback((timeframe) => {
    generatePrediction(timeframe, () => setAuthDialog(true), () => setPaymentDialog(true));
  }, [generatePrediction]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!assetData) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-gray-400">Asset not found</div>
      </div>
    );
  }

  const defaultPrice = assetData.asset.price.toString();

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Asset Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-5xl font-bold text-white mono" data-testid="asset-symbol">
                  {assetData.asset.symbol}
                </h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  assetData.asset.type === 'crypto' ? 'bg-[#6366F1]/20 text-[#6366F1]' : 'bg-[#00E0FF]/20 text-[#00E0FF]'
                }`}>
                  {assetData.asset.type.toUpperCase()}
                </span>
              </div>
              <p className="text-xl text-gray-400">{assetData.asset.name}</p>
            </div>

            <div className="flex gap-3">
              <AssetAlertDialog
                open={alertDialog}
                onOpenChange={setAlertDialog}
                defaultPrice={defaultPrice}
                onCreate={createAlert}
              />
              <AssetPortfolioDialog
                open={portfolioDialog}
                onOpenChange={setPortfolioDialog}
                defaultPrice={defaultPrice}
                onAdd={addToPortfolio}
              />
            </div>
          </div>
        </motion.div>

        <AssetPriceCard asset={assetData.asset} />

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="glass-effect mb-8">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-white" style={{ fontFamily: 'Unbounded' }}>ADVANCED CHART</CardTitle>
                {assetData.data_source === 'real' && (
                  <span className="text-xs text-[#00FFA3]">Real market data</span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {ohlcData && ohlcData.length > 0 ? (
                <CandlestickChart
                  symbol={symbol}
                  ohlcData={ohlcData}
                  indicators={indicatorData}
                  onTimeframeChange={setChartDays}
                  currentDays={chartDays}
                  onParamsChange={handleIndicatorParamsChange}
                />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={assetData.historical}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis
                      dataKey="date"
                      stroke="#64748B"
                      tick={{ fill: '#64748B', fontSize: 12 }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis
                      stroke="#64748B"
                      tick={{ fill: '#64748B', fontSize: 12 }}
                      domain={['dataMin - 5', 'dataMax + 5']}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Line type="monotone" dataKey="price" stroke="#00E0FF" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <AssetActiveAlerts alerts={alerts} onDelete={deleteAlert} />

        {/* Risk Disclaimer Banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-6"
        >
          <div className="glass-effect border-[#F59E0B]/30 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-300 leading-relaxed">
                <span className="font-semibold text-[#F59E0B]">Risk Warning:</span> AI predictions are for informational purposes only and do not constitute financial advice. Trading involves substantial risk of loss. Always conduct your own research and consult with financial professionals.
                <button
                  onClick={() => navigate('/disclaimer')}
                  className="text-[#00E0FF] hover:underline ml-1"
                >
                  Read full disclaimer
                </button>
              </p>
            </div>
          </div>
        </motion.div>

        <AssetPredictionCard
          prediction={prediction}
          predictionLoading={predictionLoading}
          onGenerate={handleGenerate}
        />
      </main>

      <AuthDialog open={authDialog} onClose={() => setAuthDialog(false)} />
      <PaymentDialog open={paymentDialog} onClose={() => setPaymentDialog(false)} />
    </div>
  );
}
